import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Grid,
  TextField, Button, Select, MenuItem, FormControl,
  InputLabel, CircularProgress, Alert, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Tooltip
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { api, apiRoutes } from '../../../api';
import { MapContainer, TileLayer, GeoJSON, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const NeighborhoodObservations = () => {
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [selectedNeighborhood, setSelectedNeighborhood] = useState('');
  const [selectedNeighborhoodData, setSelectedNeighborhoodData] = useState(null);
  const [formData, setFormData] = useState({
    mean_rent_price: '',
    mean_sales_price: '',
    infrastructure_quality: '',
    safety_level: '',
    comment: ''
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mapKey, setMapKey] = useState(0);

  useEffect(() => {
    loadAgentNeighborhoods();
    // Load the assigned area once when the tab mounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAgentNeighborhoods = async () => {
    try {
      const token = localStorage.getItem('access_token');

      // Declare agentResponse here
      const agentResponse = await api.get(apiRoutes.agents.profile, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!agentResponse.data?.neighborhoods) {
        throw new Error('Failed to load agent neighborhoods');
      }

      const neighborhoods = agentResponse.data.neighborhoods;

      // Fetch observation data for each neighborhood
      const neighborhoodsWithObservations = await Promise.all(
        neighborhoods.map(async (n) => {
          try {
            const response = await api.get(apiRoutes.agents.neighborhoods, {
              params: { neighborhood: n.id },
              headers: { Authorization: `Bearer ${token}` }
            });
            return {
              ...n,
              observation: response.data.length > 0 ? response.data[0] : null
            };
          } catch {
            return { ...n, observation: null };
          }
        })
      );

      setNeighborhoods(neighborhoodsWithObservations);

      if (neighborhoodsWithObservations.length > 0) {
        setSelectedNeighborhood(neighborhoodsWithObservations[0].id);
        loadNeighborhoodBoundary(neighborhoodsWithObservations[0].id);
        setFormData(neighborhoodsWithObservations[0].observation || {
          mean_rent_price: '',
          mean_sales_price: '',
          infrastructure_quality: '',
          safety_level: '',
          comment: ''
        });
      } else {
        setError('هیچ محله‌ای به شما تخصیص داده نشده است');
      }
    } catch (err) {
      setError('خطا در بارگذاری محله‌های تخصیص یافته');
    } finally {
      setLoading(false);
    }
  };

  const loadNeighborhoodBoundary = async (neighborhoodId) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await api.get(apiRoutes.locations.neighborhoodBoundary(neighborhoodId), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedNeighborhoodData(response.data);
      setMapKey(prev => prev + 1);
    } catch (err) {

      setSelectedNeighborhoodData(null);
    }
  };

  const loadNeighborhoodData = async (neighborhoodId) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await api.get(apiRoutes.agents.neighborhoods, {
        params: { neighborhood: neighborhoodId },
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data && response.data.length > 0) {
        const data = response.data[0];
        setFormData({
          id: data.id || null,
          mean_rent_price: data.mean_rent_price || '',
          mean_sales_price: data.mean_sales_price || '',
          infrastructure_quality: data.infrastructure_quality || '',
          safety_level: data.safety_level || '',
          comment: data.comment || ''
        });
      } else {
        setFormData({
          id: null,
          mean_rent_price: '',
          mean_sales_price: '',
          infrastructure_quality: '',
          safety_level: '',
          comment: ''
        });
      }
    } catch {
      setFormData({
        id: null,
        mean_rent_price: '',
        mean_sales_price: '',
        infrastructure_quality: '',
        safety_level: '',
        comment: ''
      });
    }
  };

  const handleNeighborhoodChange = async (event) => {
    const newNeighborhood = event.target.value;
    setSelectedNeighborhood(newNeighborhood);
    setLoading(true);
    await Promise.all([
      loadNeighborhoodData(newNeighborhood),
      loadNeighborhoodBoundary(newNeighborhood)
    ]);
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      const token = localStorage.getItem('access_token');
      const payload = {
        neighborhood: selectedNeighborhood,
        mean_rent_price: formData.mean_rent_price,
        mean_sales_price: formData.mean_sales_price,
        infrastructure_quality: formData.infrastructure_quality,
        safety_level: formData.safety_level,
        comment: formData.comment
      };

      const method = formData.id ? 'put' : 'post';
      const url = formData.id
        ? apiRoutes.agents.neighborhoodObservation(formData.id)
        : apiRoutes.agents.neighborhoods;

      await api[method](url, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      await loadAgentNeighborhoods();
      setSuccess('اطلاعات برای بررسی تیم GIS ارسال شد.');
    } catch (err) {
      setError(err.response?.data?.error || 'خطا در ذخیره اطلاعات');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const getInfrastructureQualityLabel = (value) => {
    const labels = {
      poor: 'ضعیف',
      fair: 'متوسط',
      good: 'خوب',
      excellent: 'عالی'
    };
    return labels[value] || '-';
  };

  const getSafetyLevelLabel = (value) => {
    const labels = {
      1: '1 (خیلی ناامن)',
      2: '2',
      3: '3',
      4: '4',
      5: '5 (خیلی امن)'
    };
    return labels[value] || '-';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  const MapCenter = ({ center }) => {
    const map = useMap();
    useEffect(() => {
      if (center) {
        map.setView([center.lat, center.lon], 13);
      }
    }, [center, map]);
    return null;
  };

  return (
    <Box
      dir="rtl"
      component="form"
      onSubmit={handleSubmit}
      className="agents-section agents-neighborhoods"
      sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}
    >
      <Box
        className="agents-section-head"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        sx={{ borderBottom: '1px solid #ddd', pb: 1 }}
      >
        <Typography variant="h5" component="h1" fontWeight="700">
          مشاهدات محله (گزاره های قابل نقل)
        </Typography>
        <Tooltip title="بارگذاری مجدد محله‌ها">
          <IconButton
            aria-label="بارگذاری مجدد محله‌ها"
            onClick={loadAgentNeighborhoods}
            size="large"
          >
            <RefreshIcon fontSize="medium" />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} variant="outlined">
          {error}
        </Alert>
      )}
      {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}

      <Grid container spacing={4}>
        {/* Form Section */}
        <Grid item xs={12} md={6}>
          <Card
            className="agents-form-card"
            variant="outlined"
            sx={{
              p: 3,
              mb: 4,
              borderRadius: 2,
              boxShadow: 2,
              transition: 'box-shadow 0.3s ease',
              '&:hover': { boxShadow: 6 }
            }}
          >
            <Grid container spacing={3}>
              <Grid item xs={12} mb={2}>
                <FormControl fullWidth variant="outlined" size="medium">
                  <InputLabel id="neighborhood-select-label">انتخاب محله</InputLabel>
                  <Select
                    labelId="neighborhood-select-label"
                    value={selectedNeighborhood}
                    onChange={handleNeighborhoodChange}
                    label="انتخاب محله"
                    required
                  >
                    {neighborhoods.map((neighborhood) => (
                      <MenuItem key={neighborhood.id} value={neighborhood.id}>
                        {neighborhood.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="میانگین اجاره بها (تومان)"
                  name="mean_rent_price"
                  type="number"
                  value={formData.mean_rent_price}
                  onChange={handleChange}
                  fullWidth
                  placeholder="مثلاً: 10000000"
                  inputProps={{ min: 0 }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="میانگین قیمت فروش (تومان)"
                  name="mean_sales_price"
                  type="number"
                  value={formData.mean_sales_price}
                  onChange={handleChange}
                  fullWidth
                  placeholder="مثلاً: 250000000"
                  inputProps={{ min: 0 }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel id="infrastructure-quality-label">کیفیت زیرساخت</InputLabel>
                  <Select
                    labelId="infrastructure-quality-label"
                    name="infrastructure_quality"
                    value={formData.infrastructure_quality}
                    onChange={handleChange}
                    label="کیفیت زیرساخت"
                  >
                    <MenuItem value=""><em>انتخاب کنید</em></MenuItem>
                    <MenuItem value="poor">ضعیف</MenuItem>
                    <MenuItem value="fair">متوسط</MenuItem>
                    <MenuItem value="good">خوب</MenuItem>
                    <MenuItem value="excellent">عالی</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel id="safety-level-label">سطح ایمنی</InputLabel>
                  <Select
                    labelId="safety-level-label"
                    name="safety_level"
                    value={formData.safety_level}
                    onChange={handleChange}
                    label="سطح ایمنی"
                  >
                    <MenuItem value=""><em>انتخاب کنید</em></MenuItem>
                    <MenuItem value={1}>1 (خیلی ناامن)</MenuItem>
                    <MenuItem value={2}>2</MenuItem>
                    <MenuItem value={3}>3</MenuItem>
                    <MenuItem value={4}>4</MenuItem>
                    <MenuItem value={5}>5 (خیلی امن)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="توضیحات"
                  name="comment"
                  value={formData.comment}
                  onChange={handleChange}
                  multiline
                  rows={2}
                  fullWidth
                  placeholder="توضیحات تکمیلی یا نکات مهم"
                />
              </Grid>

              <Grid item xs={12} textAlign="center" mt={2}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  type="submit"
                  disabled={isSubmitting}
                  sx={{ px: 5, fontWeight: 'bold' }}
                >
                  {isSubmitting ? 'در حال ذخیره...' : 'ذخیره تغییرات'}
                </Button>
              </Grid>
            </Grid>
          </Card>

          <Grid container spacing={3} className="agents-neighborhood-results">
  {/* Table - Left */}
  <Grid item xs={12} md={6}>
    <Paper
      elevation={2}
      sx={{ maxHeight: 400, overflowY: 'auto', borderRadius: 2 }}
    >
      <TableContainer>
              <Table stickyHeader size="small" aria-label="جدول مشاهدات محله‌ها">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>نام محله</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>میانگین اجاره</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>میانگین فروش</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>کیفیت زیرساخت</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>سطح ایمنی</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>بررسی GIS</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {neighborhoods.map((n) => {
                    const isSelected = n.id === selectedNeighborhood;
                    return (
                      <TableRow
                        key={n.id}
                        hover
                        selected={isSelected}
                        onClick={() => handleNeighborhoodChange({ target: { value: n.id } })}
                        sx={{
                          cursor: 'pointer',
                          backgroundColor: isSelected ? 'primary.light' : 'inherit',
                          '&:hover': {
                            backgroundColor: isSelected ? 'primary.light' : 'grey.100',
                          }
                        }}
                      >
                        <TableCell component="th" scope="row">{n.name}</TableCell>
                        <TableCell align="right">
                          {n.observation?.mean_rent_price
                            ? `${parseInt(n.observation.mean_rent_price).toLocaleString()} تومان`
                            : '-'}
                        </TableCell>
                        <TableCell align="right">
                          {n.observation?.mean_sales_price
                            ? `${parseInt(n.observation.mean_sales_price).toLocaleString()} تومان`
                            : '-'}
                        </TableCell>
                        <TableCell align="right">{getInfrastructureQualityLabel(n.observation?.infrastructure_quality)}</TableCell>
                        <TableCell align="right">{getSafetyLevelLabel(n.observation?.safety_level)}</TableCell>
                        <TableCell align="right">{{ pending_review: 'در انتظار', approved: 'تأیید شده', rejected: 'رد شده', superseded: 'جایگزین شده' }[n.observation?.review_status] || '-'}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
    </Paper>
  </Grid>

{/* Map - Right */}
<Grid item xs={12} md={6}>
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          نقشه محله
        </Typography>
        {selectedNeighborhoodData ? (
          <Box sx={{ height: 300, width: '100%' }}>
            <MapContainer
              key={mapKey}
              center={
                selectedNeighborhoodData.center
                  ? [selectedNeighborhoodData.center.lat, selectedNeighborhoodData.center.lon]
                  : [35.6892, 51.389]
              }
              zoom={13}
              style={{ height: '100%', width: '100%' }}
              scrollWheelZoom={false}
            >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; OpenStreetMap contributors'
                  />
                  {selectedNeighborhoodData.center && <MapCenter center={selectedNeighborhoodData.center} />}
                  {selectedNeighborhoodData.boundary && (
                    <GeoJSON
                      data={selectedNeighborhoodData.boundary}
                      style={{ color: '#d32f2f', weight: 3, fillColor: '#f44336', fillOpacity: 0.3 }}
                    />
                  )}
            </MapContainer>
          </Box>
        ) :  (
          <Box
            sx={{
              height: '400px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              color: 'text.secondary',
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {selectedNeighborhoodData && (
          <Box mt={3}>
            <Typography variant="subtitle1" fontWeight="600" mb={0.5}>
              اطلاعات محله:
            </Typography>
            <Typography variant="body2" color="text.secondary" lineHeight={1.4}>
              {selectedNeighborhoodData.name}، منطقه {selectedNeighborhoodData.region}، شهر {selectedNeighborhoodData.city}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  </Grid>
</Grid>
        </Grid>

      </Grid>
    </Box>
  );
}

export default NeighborhoodObservations;
